import React, { useState, useRef, useEffect } from 'react';
import { useFont } from '../../Design/FontContext';
import { titleFonts, textFonts } from '../../utils/fontsHelper'; // ✅ Import aus zentraler Quelle
import './FontSelector.css';

// ✅ AKTUALISIERTE FONT-LISTEN aus fontsHelper.js
const fontOptions = titleFonts; // Alle Title/Display Fonts inkl. Voyage und Main-Fonts
const smallFontOptions = textFonts; // Alle Text/Body Fonts

function FontSelector({ 
    label, 
    fieldName, 
    currentFont, 
    onChange, 
    fontType = 'big', // 'big' oder 'small'
    containerRef
}) {
    const [isOpen, setIsOpen] = useState(false);
    const fonts = useFont();
    
    // Bestimme welche Font-Liste verwendet werden soll
    const systemFonts = fontType === 'big' ? fontOptions : smallFontOptions;
    
    // Kombiniere System-Fonts mit Custom-Fonts
    const customFontNames = fonts.customFonts?.map(f => f.name) || [];
    const availableFonts = [...systemFonts, ...customFontNames];
    
    // Bestimme aktuell ausgewählte globale Font
    const globalFont = fontType === 'big' ? fonts.globalBigFont : fonts.globalSmallFont;
    
    // Verwende currentFont falls gesetzt, sonst globale Font
    const displayFont = currentFont || globalFont || (fontType === 'big' ? 'Arial' : 'Jost');

    // Helper-Funktion um Custom Fonts zu erkennen
    const isCustomFont = (fontName) => {
        return customFontNames.includes(fontName);
    };
    
    // Debug: Disabled by default to reduce console noise
    // useEffect(() => {
    //     console.log(`🎨 FontSelector [${label}] - Custom Fonts available:`, customFontNames);
    // }, [fonts.customFonts]);

    const handleFontSelect = (selectedFont) => {
        onChange({ target: { name: fieldName, value: selectedFont } });
        setIsOpen(false);
    };

    return (
        <div className="font-selector-container">
            <label className="font-selector-label">{label}</label>
            
            {/* Font Display & Trigger */}
            <div 
                className="font-selector-trigger"
                onClick={() => setIsOpen(!isOpen)}
            >
                <span 
                    className="font-selector-current"
                    style={{ fontFamily: displayFont }}
                >
                    {displayFont}
                </span>
                <svg 
                    className={`font-selector-arrow ${isOpen ? 'open' : ''}`}
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2"
                >
                    <polyline points="6,9 12,15 18,9"></polyline>
                </svg>
            </div>

            
            {/* Overlay zum Schließen */}
            {isOpen && (
                <div 
                    className="font-selector-overlay"
                    onClick={() => setIsOpen(false)}
                />
            )}

            {/* Font Dropdown */}
            {isOpen && (
                <div className="font-selector-dropdown">
                    <div className="font-selector-section">
                        <div className="font-selector-section-title">
                            Current: {globalFont || 'Default'}
                        </div>
                        <div 
                            className={`font-selector-option ${(!currentFont || currentFont === globalFont) ? 'selected' : ''}`}
                            onClick={() => handleFontSelect('')}
                            style={{ fontFamily: globalFont }}
                        >
                            Use Global ({globalFont || 'Default'})
                        </div>
                    </div>
                    
                    {/* Custom Fonts Section */}
                    {customFontNames.length > 0 && (
                        <div className="font-selector-section">
                            <div className="font-selector-section-title">
                                <i className="fas fa-star" style={{ color: '#667eea', marginRight: '6px' }}></i>
                                Custom Fonts
                            </div>
                            <div className="font-selector-grid">
                                {customFontNames.map(font => (
                                    <div 
                                        key={`custom-${font}`} 
                                        className={`font-selector-option custom-font ${currentFont === font ? 'selected' : ''}`}
                                        onClick={() => handleFontSelect(font)}
                                        style={{ fontFamily: font }}
                                        title="Custom uploaded font"
                                    >
                                        {font}
                                        <span className="custom-font-badge">✨</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="font-selector-section">
                        <div className="font-selector-section-title">
                            {fontType === 'big' ? 'Title Fonts' : 'Text Fonts'}
                        </div>
                        <div className="font-selector-grid">
                            {systemFonts.map(font => (
                                <div 
                                    key={font} 
                                    className={`font-selector-option ${currentFont === font ? 'selected' : ''}`}
                                    onClick={() => handleFontSelect(font)}
                                    style={{ fontFamily: font }}
                                >
                                    {font}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* ✅ ERWEITERT: Zusätzliche Font-Optionen für beide Font-Types */}
                    {fontType === 'small' && (
                        <div className="font-selector-section">
                            <div className="font-selector-section-title">
                                Title Fonts (Alternative)
                            </div>
                            <div className="font-selector-grid">
                                {fontOptions.map(font => (
                                    <div 
                                        key={`title-${font}`} 
                                        className={`font-selector-option ${currentFont === font ? 'selected' : ''}`}
                                        onClick={() => handleFontSelect(font)}
                                        style={{ fontFamily: font }}
                                    >
                                        {font}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* ✅ NEU: Zusätzliche Text Fonts für Header/Title-Inputs */}
                    {fontType === 'big' && (
                        <div className="font-selector-section">
                            <div className="font-selector-section-title">
                                Text Fonts (Alternative)
                            </div>
                            <div className="font-selector-grid">
                                {smallFontOptions.map(font => (
                                    <div 
                                        key={`text-${font}`} 
                                        className={`font-selector-option ${currentFont === font ? 'selected' : ''}`}
                                        onClick={() => handleFontSelect(font)}
                                        style={{ fontFamily: font }}
                                    >
                                        {font}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default FontSelector;
